import React, { Component } from 'react';
import download from 'downloadjs';
import * as XLSX from 'xlsx';
import api from 'config';
import cm from 'helpers/commons';
import request from 'helpers/request';
import toastr from 'helpers/toastr';
import divisionExcelParser from './DivisionManagementByExcelParser';
import divisionExcelUpload from './DivisionManagementByExcelUpload';

const { readDivisionExcelFile } = divisionExcelParser;
const { DIVISION_UPLOAD_URL, normalizeDivisionUploadResponse } = divisionExcelUpload;

const INITIAL_STATS = { total: 0, success: 0, duplicate: 0, fail: 0 };
const MIN_DEPARTMENT_COLUMN_COUNT = 3;
const DIVISION_TEMPLATE_DOWNLOAD_URL = '/company/v1/division/upload/download';

// screen: 'idle' | 'fileSelected' | 'uploaded'
class DivisionManagementByExcel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      screen: 'idle',
      isDragOver: false,
      file: null,
      results: [],
      stats: INITIAL_STATS,
      maxDepartmentDepth: MIN_DEPARTMENT_COLUMN_COUNT,
      hasExternalTitle: false,
      isTemplateDownloading: false,
      isUploading: false
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    const mainTitleEl = document.getElementsByClassName('main-title')[0];
    if (mainTitleEl) {
      cm.mainTitle('사용자 관리 > 부서 일괄 등록');
      this.setState({ hasExternalTitle: true });
    }
  }

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ isDragOver: true });
  };

  handleDragLeave = () => {
    this.setState({ isDragOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ isDragOver: false });
    const file = e.dataTransfer.files[0];
    if (file) this.handleExcelFile(file);
  };

  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) this.handleExcelFile(file);
    e.target.value = '';
  };

  handleFileSelect = () => {
    this.fileInputRef.current.click();
  };

  handleFileDelete = () => {
    this.setState({
      file: null,
      screen: 'idle',
      results: [],
      stats: INITIAL_STATS,
      maxDepartmentDepth: MIN_DEPARTMENT_COLUMN_COUNT
    });
  };

  handleExcelFile = async (file) => {
    this.setState({
      file,
      screen: 'fileSelected',
      results: [],
      stats: INITIAL_STATS,
      maxDepartmentDepth: MIN_DEPARTMENT_COLUMN_COUNT
    });

    try {
      const { rows, stats, maxDepartmentDepth } = await readDivisionExcelFile(file);

      if (this.state.file !== file) return;

      this.setState({
        results: rows,
        stats,
        maxDepartmentDepth: Math.max(maxDepartmentDepth, MIN_DEPARTMENT_COLUMN_COUNT)
      });

      if (this.props.onFileParsed) {
        this.props.onFileParsed({ file, rows, stats, maxDepartmentDepth });
      }
    } catch (error) {
      console.error(error);
      this.setState({
        file: null,
        screen: 'idle',
        results: [],
        stats: INITIAL_STATS,
        maxDepartmentDepth: MIN_DEPARTMENT_COLUMN_COUNT
      });
      toastr('top-right', 'warning', '엑셀 파일을 읽지 못했습니다. 파일 형식을 확인해 주세요.');
    }
  };

  handlePrev = () => {
    if (this.props.onPrev) this.props.onPrev();
    else window.history.back();
  };

  handleSave = async () => {
    const { file, isUploading } = this.state;

    if (this.props.onSave) {
      this.props.onSave(this.state.file, this.state.results);
      return;
    }

    if (!file) {
      toastr('top-right', 'warning', '업로드할 엑셀 파일을 선택해 주세요.');
      return;
    }

    if (isUploading) {
      toastr('top-right', 'warning', '부서 등록을 진행 중입니다.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    this.setState({ isUploading: true });

    try {
      const response = await request({
        url: `${api.config.companyAPI}${DIVISION_UPLOAD_URL}`,
        method: 'post',
        data: formData,
        type: 'file'
      });
      const { rows, stats, maxDepartmentDepth } = normalizeDivisionUploadResponse(response.data);

      this.setState({
        screen: 'uploaded',
        results: rows,
        stats,
        maxDepartmentDepth: Math.max(maxDepartmentDepth, MIN_DEPARTMENT_COLUMN_COUNT)
      });
      toastr('top-right', 'success', '부서 일괄 등록 결과를 확인해 주세요.');
    } catch (error) {
      const message =
        error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : '부서 일괄 등록에 실패했습니다.';
      console.log('###### DivisionUpload : ', error);
      toastr('top-right', 'error', message);
    } finally {
      this.setState({ isUploading: false });
    }
  };

  handleDownloadTemplate = async () => {
    const { isTemplateDownloading } = this.state;

    if (this.props.onDownloadTemplate) {
      this.props.onDownloadTemplate();
      return;
    }

    if (isTemplateDownloading) {
      toastr('top-right', 'warning', '부서 등록 양식을 다운로드 중입니다.');
      return;
    }

    this.setState({ isTemplateDownloading: true });

    try {
      const response = await request({
        url: `${api.config.companyAPI}${DIVISION_TEMPLATE_DOWNLOAD_URL}`,
        method: 'get',
        type: 'excel'
      });
      const fileName = '부서일괄등록양식.xlsx';

      download(response.data, fileName);
      toastr('top-right', 'success', '부서 등록 양식 다운로드가 완료되었습니다.');
    } catch (error) {
      console.log('###### DivisionTemplateDownload : ', error);
      toastr('top-right', 'error', '부서 등록 양식 다운로드에 실패했습니다.');
    } finally {
      this.setState({ isTemplateDownloading: false });
    }
  };

  handleDownloadResult = () => {
    if (this.props.onDownloadResult) {
      this.props.onDownloadResult();
      return;
    }

    const { results, maxDepartmentDepth, screen } = this.state;

    const departmentColumns = Array.from(
      { length: Math.max(maxDepartmentDepth, MIN_DEPARTMENT_COLUMN_COUNT) },
      (_, index) => index + 1
    );

    const statusLabel = (status) => {
      if (status === 'success') return '성공';
      if (status === 'duplicate') return '중복';
      if (status === 'fail') return '실패';
      return '-';
    };

    const headers = [...departmentColumns.map((n) => `부서${n}`), '상태', '결과 메세지'];
    const rows = results.map((row) => [
      ...departmentColumns.map((n) => row[`dept${n}`] || '-'),
      screen === 'uploaded' ? statusLabel(row.status) : '-',
      screen === 'uploaded' ? row.message || '-' : '-'
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '결과');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    download(new Blob([excelBuffer], { type: 'application/octet-stream' }), '부서일괄등록결과.xlsx');
  };

  renderDropzone() {
    const { screen, isDragOver, file } = this.state;

    if (screen === 'idle') {
      return (
        <div
          className={`division-excel-dropzone${isDragOver ? ' active' : ''}`}
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
        >
          <input
            ref={this.fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="division-excel-file-input"
            onChange={this.handleFileChange}
          />
          <p className="division-excel-drop-text">파일을 이곳에 끌어다 놓으세요.</p>
          <p className="division-excel-drop-text">또는 파일 선택 버튼을 눌러 업로드 해주세요.</p>
          <button type="button" className="division-excel-file-button" onClick={this.handleFileSelect}>
            파일 선택
          </button>
        </div>
      );
    }

    if (screen === 'fileSelected') {
      return (
        <div className="division-excel-dropzone">
          <input
            ref={this.fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="division-excel-file-input"
            onChange={this.handleFileChange}
          />
          <p className="division-excel-file-name">{file && file.name}</p>
          <div className="division-excel-file-actions">
            <button type="button" className="division-excel-delete-button" onClick={this.handleFileDelete}>
              초기화
            </button>
          </div>
        </div>
      );
    }

    // uploaded
    return (
      <div className="division-excel-dropzone">
        <p className="division-excel-file-name">{file && file.name}</p>
      </div>
    );
  }

  render() {
    const {
      screen,
      results,
      stats,
      hasExternalTitle,
      maxDepartmentDepth,
      isTemplateDownloading,
      isUploading,
      file
    } = this.state;
    const departmentColumns = Array.from(
      { length: Math.max(maxDepartmentDepth, MIN_DEPARTMENT_COLUMN_COUNT) },
      (_, index) => index + 1
    );

    return (
      <main className="division-excel-page" style={{ minHeight: 'calc(100vh - 40px)' }}>
        <section className="division-excel-body">
          {!hasExternalTitle && (
            <>
              <h1 className="division-excel-title" style={{ fontSize: '1.857rem', fontWeight: 400, marginBottom: 16 }}>
                사용자 관리 &gt; 부서 일괄 등록
              </h1>
              <div
                className="division-excel-title-line"
                style={{ borderTop: '1px solid rgba(0,0,0,0.5)', marginBottom: 16 }}
              />
            </>
          )}

          {this.renderDropzone()}

          <p className="division-excel-helper">* 반드시 제공된 양식 파일을 사용하여 작성해 주세요.</p>

          <h2 className="division-excel-section-title">목록</h2>

          <div className="division-excel-section-line" />

          <p className="division-excel-count">
            총 ({stats.total}) : 성공({stats.success}) / 중복({stats.duplicate}) / 실패({stats.fail})
          </p>

          <div className="division-excel-result">
            {results.length === 0 ? (
              <p className="division-excel-empty">결과가 없습니다.</p>
            ) : (
              <table className="division-excel-table">
                <thead>
                  <tr>
                    {departmentColumns.map((columnNumber) => (
                      <th key={columnNumber}>부서{columnNumber}</th>
                    ))}
                    <th>상태</th>
                    <th>결과 메세지</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row, i) => (
                    <tr key={row.rowNumber || i}>
                      {departmentColumns.map((columnNumber) => (
                        <td key={columnNumber}>{row[`dept${columnNumber}`] || '-'}</td>
                      ))}
                      <td>
                        {screen === 'uploaded' ? (
                          <span>
                            {row.status === 'success'
                              ? '성공'
                              : row.status === 'duplicate'
                              ? '중복'
                              : row.status === 'fail'
                              ? '실패'
                              : '-'}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="division-excel-reason">{screen === 'uploaded' ? row.message || '-' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <div
          className="division-excel-actions"
          style={{ position: 'fixed', bottom: 20, left: 260, right: 20, margin: 0 }}
        >
          <div className="division-excel-action-left">
            <button
              type="button"
              className="division-excel-template-button"
              onClick={this.handleDownloadTemplate}
              disabled={isTemplateDownloading}
            >
              {isTemplateDownloading ? '다운로드 중...' : '부서 일괄 등록 엑셀 서식 다운로드'}
            </button>
            {screen === 'uploaded' && (
              <button className="division-excel-template-button" type="button" onClick={this.handleDownloadResult}>
                결과 엑셀 다운로드
              </button>
            )}
          </div>

          <div className="division-excel-action-right">
            <button type="button" className="division-excel-prev-button" onClick={this.handlePrev}>
              이전
            </button>
            <button
              type="button"
              className="division-excel-save-button"
              onClick={this.handleSave}
              disabled={!file || screen === 'uploaded' || isUploading}
            >
              {isUploading ? '저장 중...' : '저장하기'}
            </button>
          </div>
        </div>
      </main>
    );
  }
}

export default DivisionManagementByExcel;
